/**
 * Gráficos del resumen.
 *
 * Cada tarjeta deja elegir entre cuatro formas de leer el mismo dato —
 * barras, puntos, dona y líneas — y recuerda la elección.
 *
 * Sobre el color: la paleta es una escala del propio azul petróleo, no un
 * arcoíris. Basta para separar categorías y no compite con los flúor, que
 * están reservados para estado. El acento verde marca el dato de hoy.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import {
  BarChart3,
  BarChartHorizontalBig,
  ChartPie,
  LineChart as IconoLinea,
  ScatterChart as IconoPuntos,
} from 'lucide-react';
import type { DocGuia } from '@/db/tiposDatos';
import { m3 as fm3, fecha as ffecha } from '@/domain/formato';
import { MINUTOS_LIBRES_ESTADIA, formatearMinutos } from '@/domain/tiempos';
import { Tarjeta, TarjetaTitulo, Vacio } from '@/components/ui';
import { cn } from '@/lib/cn';

// ── Color ─────────────────────────────────────────────────────────────────

/** Escala del azul petróleo, de claro a oscuro. Legible sobre el fondo. */
export const SERIE = [
  '#7FD4E8',
  '#4BA3B8',
  '#2A7D91',
  '#A8E4F0',
  '#1B6273',
  '#C9E5ED',
  '#14505F',
  '#5FBDD4',
  '#0E3A46',
];

const ACENTO = '#4BA3B8';
const DESTACADO = '#2BFF88';

export const COLOR_PLANTA: Record<string, string> = {
  'NV 17': '#7FD4E8',
  'KM 13': '#2A7D91',
  VALLE: '#C9E5ED',
};

const EJE = { fill: '#8FB3BD', fontSize: 11 };
const CUADRICULA = 'rgba(255,255,255,.07)';

/**
 * Estilo del recuadro emergente.
 * Recharts deja el texto en su gris oscuro por defecto, ilegible sobre el
 * fondo petróleo: hay que fijar el color en los tres niveles.
 */
export const TOOLTIP = {
  contentStyle: {
    background: '#0A2A33',
    border: '1px solid rgba(255,255,255,.18)',
    borderRadius: 10,
    fontSize: 12,
    color: '#EAF4F7',
    boxShadow: '0 8px 24px -12px rgba(0,0,0,.7)',
    padding: '8px 10px',
  },
  labelStyle: { color: '#8FB3BD', marginBottom: 4, fontWeight: 600 },
  itemStyle: { color: '#EAF4F7', padding: 0 },
  cursor: { fill: 'rgba(255,255,255,.05)' },
} as const;

/** Recuadro propio: garantiza el contraste sin depender de los estilos internos. */
export function Caja({ titulo, filas }: { titulo: ReactNode; filas: [string, string][] }) {
  return (
    <div className="rounded-[10px] border border-white/15 bg-petroleo-900 px-3 py-2 text-xs shadow-xl">
      <div className="font-semibold text-texto">{titulo}</div>
      {filas.map(([k, v]) => (
        <div key={k} className="mt-1 flex items-center justify-between gap-4">
          <span className="text-texto-mute">{k}</span>
          <span className="font-mono tabular-nums text-texto">{v}</span>
        </div>
      ))}
    </div>
  );
}

// ── Selector de tipo ──────────────────────────────────────────────────────

export type TipoGrafico = 'barrasH' | 'barras' | 'puntos' | 'dona' | 'lineas';

const TIPOS: { id: TipoGrafico; texto: string; icono: typeof BarChart3 }[] = [
  { id: 'barrasH', texto: 'Barras horizontales', icono: BarChartHorizontalBig },
  { id: 'barras', texto: 'Barras verticales', icono: BarChart3 },
  { id: 'puntos', texto: 'Puntos', icono: IconoPuntos },
  { id: 'dona', texto: 'Dona', icono: ChartPie },
  { id: 'lineas', texto: 'Líneas', icono: IconoLinea },
];

const CLAVE = 'xtreme:graficos:v1';

/** Recuerda el tipo elegido para cada gráfico. */
export function useTipoGrafico(id: string, inicial: TipoGrafico, permitidos: TipoGrafico[]) {
  const [tipo, setTipo] = useState<TipoGrafico>(inicial);

  useEffect(() => {
    try {
      const g = JSON.parse(localStorage.getItem(CLAVE) ?? '{}') as Record<string, TipoGrafico>;
      const guardado = g[id];
      if (guardado && permitidos.includes(guardado)) setTipo(guardado);
    } catch {
      // Sin preferencia guardada: queda el valor inicial.
    }
  }, [id, permitidos]);

  const cambiar = useCallback(
    (t: TipoGrafico) => {
      setTipo(t);
      try {
        const g = JSON.parse(localStorage.getItem(CLAVE) ?? '{}') as Record<string, TipoGrafico>;
        localStorage.setItem(CLAVE, JSON.stringify({ ...g, [id]: t }));
      } catch {
        // Si no se puede guardar, al menos cambia en esta sesión.
      }
    },
    [id],
  );

  return [tipo, cambiar] as const;
}

export function SelectorTipo({
  tipo,
  alCambiar,
  disponibles = TIPOS.map((t) => t.id),
}: {
  tipo: TipoGrafico;
  alCambiar: (t: TipoGrafico) => void;
  disponibles?: TipoGrafico[];
}) {
  return (
    <div className="flex gap-0.5 rounded-control bg-petroleo-800/70 p-0.5">
      {TIPOS.filter((t) => disponibles.includes(t.id)).map(({ id, texto, icono: Icono }) => (
        <button
          key={id}
          type="button"
          title={texto}
          aria-label={texto}
          aria-pressed={tipo === id}
          onClick={() => alCambiar(id)}
          className={cn(
            'rounded-[6px] px-2 py-1 transition-colors',
            tipo === id
              ? 'bg-petroleo-500 text-texto'
              : 'text-texto-mute hover:bg-white/5 hover:text-texto',
          )}
        >
          <Icono size={14} />
        </button>
      ))}
    </div>
  );
}

// ── Datos ─────────────────────────────────────────────────────────────────

export interface Punto {
  nombre: string;
  m3: number;
}

function agrupar(guias: DocGuia[], clave: (g: DocGuia) => string | null, tope = 8): Punto[] {
  const mapa = new Map<string, number>();
  for (const g of guias) {
    const k = clave(g);
    if (!k) continue;
    mapa.set(k, (mapa.get(k) ?? 0) + (g.cantidadM3 || 0));
  }
  const filas = [...mapa.entries()]
    .map(([nombre, m3]) => ({ nombre, m3 }))
    .sort((a, b) => b.m3 - a.m3);

  if (filas.length <= tope) return filas;
  const visibles = filas.slice(0, tope);
  const resto = filas.slice(tope).reduce((s, f) => s + f.m3, 0);
  return resto > 0 ? [...visibles, { nombre: 'Otros', m3: resto }] : visibles;
}

const corto = (s: string, n = 18) => (s.length > n ? s.slice(0, n - 1) + '…' : s);

/** Ancho del eje de categorías: suficiente para leer un nombre de cliente. */
const ANCHO_ETIQUETA = 150;

// ── Gráfico genérico ──────────────────────────────────────────────────────

interface PropsGrafico {
  id: string;
  titulo: string;
  datos: Punto[];
  inicial?: TipoGrafico;
  /** Color fijo por nombre; si no, se usa la escala. */
  colorDe?: (nombre: string, i: number) => string;
  /** Índice a destacar en verde (por ejemplo, hoy). */
  destacar?: number;
  altura?: number;
  accion?: ReactNode;
  /**
   * El eje X es una línea de tiempo. Se ofrecen barras, líneas y puntos, pero
   * no la dona: repartir catorce días en un anillo no dice nada y la leyenda
   * se come el gráfico.
   */
  temporal?: boolean;
}

export function Grafico({
  id,
  titulo,
  datos,
  inicial = 'barras',
  colorDe,
  destacar,
  altura,
  accion,
  temporal,
}: PropsGrafico) {
  const permitidos: TipoGrafico[] = temporal
    ? ['barras', 'lineas', 'puntos']
    : ['barrasH', 'barras', 'puntos', 'dona', 'lineas'];
  const [tipo, setTipo] = useTipoGrafico(id, inicial, permitidos);

  const color = (nombre: string, i: number) =>
    i === destacar ? DESTACADO : (colorDe?.(nombre, i) ?? SERIE[i % SERIE.length]!);

  // Puntos y barras horizontales necesitan una fila por categoría.
  const porFila = tipo === 'puntos' || tipo === 'barrasH';
  const alto = porFila ? Math.max(200, datos.length * 34 + 54) : (altura ?? 240);

  // Con nombres largos, la barra vertical los corta: se inclinan.
  const largas = datos.some((d) => d.nombre.length > 9);

  const contenido = (() => {
    if (datos.length === 0) return <Vacio titulo="Sin datos" />;

    const emergente = (
      <Tooltip
        {...TOOLTIP}
        content={({ payload, label }) => {
          const p = payload?.[0]?.payload as Punto | undefined;
          if (!p) return null;
          return <Caja titulo={p.nombre ?? label} filas={[['Despachado', `${fm3(p.m3)} m³`]]} />;
        }}
      />
    );

    switch (tipo) {
      case 'dona':
        return (
          <PieChart>
            <Pie
              data={datos}
              dataKey="m3"
              nameKey="nombre"
              innerRadius="50%"
              outerRadius="80%"
              paddingAngle={2}
              stroke="none"
            >
              {datos.map((d, i) => (
                <Cell key={d.nombre} fill={color(d.nombre, i)} />
              ))}
            </Pie>
            <Legend
              verticalAlign="middle"
              align="right"
              layout="vertical"
              iconType="circle"
              iconSize={8}
              formatter={(v: string) => (
                <span style={{ color: '#EAF4F7', fontSize: 11 }}>{corto(String(v), 16)}</span>
              )}
            />
            {emergente}
          </PieChart>
        );

      case 'barrasH':
        return (
          <BarChart
            data={datos}
            layout="vertical"
            margin={{ top: 6, right: 26, bottom: 2, left: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={CUADRICULA} horizontal={false} />
            <XAxis type="number" tick={EJE} axisLine={false} tickLine={false} />
            <YAxis
              type="category"
              dataKey="nombre"
              tick={{ ...EJE, fill: '#C9E5ED' }}
              axisLine={false}
              tickLine={false}
              width={ANCHO_ETIQUETA}
              tickFormatter={(v: string) => corto(v, 24)}
            />
            {emergente}
            <Bar dataKey="m3" radius={[0, 4, 4, 0]} barSize={16}>
              {datos.map((d, i) => (
                <Cell key={d.nombre} fill={color(d.nombre, i)} />
              ))}
              <LabelList
                dataKey="m3"
                position="right"
                formatter={(v: number) => fm3(v)}
                style={{ fill: '#8FB3BD', fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }}
              />
            </Bar>
          </BarChart>
        );

      case 'puntos':
        return (
          <ScatterChart margin={{ top: 8, right: 20, bottom: 2, left: 0 }}>
            <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,.10)" vertical={false} />
            <XAxis type="number" dataKey="m3" tick={EJE} axisLine={false} tickLine={false} />
            <YAxis
              type="category"
              dataKey="nombre"
              tick={{ ...EJE, fill: '#C9E5ED' }}
              axisLine={false}
              tickLine={false}
              width={ANCHO_ETIQUETA}
              tickFormatter={(v: string) => corto(v, 24)}
            />
            <ZAxis range={[130, 130]} />
            {emergente}
            <Scatter data={datos} shape="circle">
              {datos.map((d, i) => (
                <Cell key={d.nombre} fill={color(d.nombre, i)} />
              ))}
            </Scatter>
          </ScatterChart>
        );

      case 'lineas':
        return (
          <AreaChart data={datos} margin={{ top: 6, right: 6, bottom: 0, left: -18 }}>
            <defs>
              <linearGradient id={`grad-${id}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={ACENTO} stopOpacity={0.45} />
                <stop offset="100%" stopColor={ACENTO} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={CUADRICULA} vertical={false} />
            <XAxis
              dataKey="nombre"
              tick={EJE}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v: string) => corto(String(v), 10)}
            />
            <YAxis tick={EJE} axisLine={false} tickLine={false} />
            {emergente}
            <Area
              type="monotone"
              dataKey="m3"
              stroke={ACENTO}
              strokeWidth={2}
              fill={`url(#grad-${id})`}
              dot={{ r: 2.5, fill: ACENTO, strokeWidth: 0 }}
              activeDot={{ r: 5, fill: DESTACADO, strokeWidth: 0 }}
            />
          </AreaChart>
        );

      default:
        return (
          <BarChart data={datos} margin={{ top: 6, right: 6, bottom: 0, left: -18 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={CUADRICULA} vertical={false} />
            <XAxis
              dataKey="nombre"
              tick={EJE}
              axisLine={false}
              tickLine={false}
              interval={0}
              angle={largas ? -35 : 0}
              textAnchor={largas ? 'end' : 'middle'}
              height={largas ? 68 : 30}
              tickFormatter={(v: string) => corto(String(v), largas ? 16 : 12)}
            />
            <YAxis tick={EJE} axisLine={false} tickLine={false} />
            {emergente}
            <Bar dataKey="m3" radius={[4, 4, 0, 0]} maxBarSize={54}>
              {datos.map((d, i) => (
                <Cell key={d.nombre} fill={color(d.nombre, i)} />
              ))}
            </Bar>
          </BarChart>
        );
    }
  })();

  return (
    <Tarjeta>
      <TarjetaTitulo
        accion={
          <div className="flex items-center gap-2">
            {accion}
            <SelectorTipo tipo={tipo} alCambiar={setTipo} disponibles={permitidos} />
          </div>
        }
      >
        {titulo}
      </TarjetaTitulo>
      <div className="p-4" style={{ height: alto }}>
        {datos.length === 0 ? (
          contenido
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {contenido}
          </ResponsiveContainer>
        )}
      </div>
    </Tarjeta>
  );
}

// ── Gráficos concretos ────────────────────────────────────────────────────

export function PorPlanta({ guias }: { guias: DocGuia[] }) {
  return (
    <Grafico
      id="planta"
      titulo="Despacho por planta"
      inicial="dona"
      datos={agrupar(guias, (g) => g.plantaCodigo)}
      colorDe={(n, i) => COLOR_PLANTA[n] ?? SERIE[i % SERIE.length]!}
      altura={224}
    />
  );
}

const NOMBRE_TURNO: Record<string, string> = { A: 'A — día', C: 'C — noche', TA: 'TA — adicional' };

export function PorTurno({ guias }: { guias: DocGuia[] }) {
  return (
    <Grafico
      id="turno"
      titulo="Despacho por turno"
      inicial="barras"
      datos={agrupar(guias, (g) => (g.turno ? (NOMBRE_TURNO[g.turno] ?? g.turno) : null))}
      altura={224}
    />
  );
}

export function PorCliente({ guias }: { guias: DocGuia[] }) {
  return (
    <Grafico
      id="cliente"
      titulo="Principales clientes"
      inicial="barrasH"
      datos={agrupar(guias, (g) => g.clienteNombre)}
    />
  );
}

export function PorProducto({ guias }: { guias: DocGuia[] }) {
  return (
    <Grafico
      id="producto"
      titulo="Productos más despachados"
      inicial="barrasH"
      datos={agrupar(guias, (g) => g.productoCodigo)}
    />
  );
}

export function PorDestino({ guias }: { guias: DocGuia[] }) {
  return (
    <Grafico
      id="destino"
      titulo="Destinos con más volumen"
      inicial="barrasH"
      datos={agrupar(guias, (g) => g.destinoTexto, 6)}
    />
  );
}

/** m³ por día de los últimos 14 días. */
/**
 * Despacho día a día, en una ventana de dos semanas.
 *
 * La ventana termina en `hasta`, que normalmente es hoy. Con un libro
 * histórico recién importado, hoy puede caer semanas después de la última
 * guía y el gráfico saldría vacío; en ese caso se ancla en la guía más
 * reciente y el título lo dice.
 */
export function DespachoDiario({
  guias,
  hoy,
  hasta,
}: {
  guias: DocGuia[];
  hoy: string;
  hasta?: string;
}) {
  const fin = hasta ?? hoy;
  const dias = Array.from({ length: 14 }, (_, i) => {
    const [a, m, d0] = fin.split('-').map(Number);
    const d = new Date(a!, m! - 1, d0!);
    d.setDate(d.getDate() - (13 - i));
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  });

  const datos = dias.map((f) => ({
    nombre: `${f.slice(8)}/${f.slice(5, 7)}`,
    fecha: f,
    m3: guias.filter((g) => g.fecha === f).reduce((s, g) => s + (g.cantidadM3 || 0), 0),
  }));

  return (
    <Grafico
      id="diario"
      titulo={
        fin === hoy
          ? 'Despacho de los últimos 14 días'
          : `Despacho · 14 días hasta el ${fin.slice(8)}-${fin.slice(5, 7)}-${fin.slice(0, 4)}`
      }
      inicial="barras"
      datos={datos}
      colorDe={() => ACENTO}
      destacar={dias.indexOf(hoy)}
      altura={252}
      temporal
    />
  );
}

/** Últimos 6 meses, con la variación contra el mes anterior. */
/**
 * Un año mes a mes, para ver la estacionalidad y comparar contra el mismo
 * período del año anterior.
 *
 * La ventana termina en `hasta` — la guía más reciente, no la fecha del
 * sistema. Eran seis meses contados desde hoy, y con un libro histórico eso
 * dejaba la mitad del gráfico en blanco.
 */
export function ComparativaMensual({ guias, hasta }: { guias: DocGuia[]; hasta?: string }) {
  const fin = hasta
    ? new Date(Number(hasta.slice(0, 4)), Number(hasta.slice(5, 7)) - 1, 1)
    : new Date();
  const meses = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(fin.getFullYear(), fin.getMonth() - (11 - i), 1);
    return {
      clave: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      nombre: d.toLocaleDateString('es-CL', { month: 'short' }).replace('.', ''),
      anio: d.getFullYear(),
    };
  });

  const datos = meses.map((m, i) => ({
    // En doce meses se cruza de año: el enero que abre uno nuevo lo dice.
    nombre: i === 0 || m.nombre === 'ene' ? `${m.nombre} ${String(m.anio).slice(2)}` : m.nombre,
    m3: guias
      .filter((g) => g.fecha.startsWith(m.clave))
      .reduce((s, g) => s + (g.cantidadM3 || 0), 0),
  }));

  const actual = datos.at(-1)?.m3 ?? 0;
  const previo = datos.at(-2)?.m3 ?? 0;
  const variacion = previo > 0 ? (actual - previo) / previo : null;

  return (
    <Grafico
      id="mensual"
      titulo="Doce meses, mes a mes"
      inicial="lineas"
      datos={datos.some((d) => d.m3 > 0) ? datos : []}
      colorDe={() => ACENTO}
      destacar={datos.length - 1}
      altura={224}
      temporal
      accion={
        variacion !== null ? (
          <span
            className={cn(
              'font-mono text-xs tabular-nums',
              variacion >= 0 ? 'text-fluor-verde' : 'text-fluor-rojo',
            )}
          >
            {variacion >= 0 ? '▲' : '▼'} {Math.abs(variacion * 100).toFixed(0)}%
          </span>
        ) : undefined
      }
    />
  );
}

// ── Dispersión de viajes ──────────────────────────────────────────────────

/** Barras de estadía promedio por tramo, en minutos. */
function GraficoTramos({ datos, horizontal }: { datos: Punto[]; horizontal: boolean }) {
  const emergente = (
    <Tooltip
      {...TOOLTIP}
      content={({ payload }) => {
        const p = payload?.[0]?.payload as Punto | undefined;
        if (!p) return null;
        return (
          <Caja
            titulo={p.nombre}
            filas={[
              ['Estadía promedio', formatearMinutos(p.m3)],
              ['Sobre 45 min libres', formatearMinutos(Math.max(0, p.m3 - MINUTOS_LIBRES_ESTADIA))],
            ]}
          />
        );
      }}
    />
  );
  const enHoras = (v: number) => (v >= 60 ? `${(v / 60).toFixed(1)} h` : `${v} min`);
  const colorTramo = (v: number) => (v > MINUTOS_LIBRES_ESTADIA ? '#EAFF00' : ACENTO);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={datos}
        layout={horizontal ? 'vertical' : 'horizontal'}
        margin={{ top: 6, right: 26, bottom: 2, left: horizontal ? 0 : -12 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={CUADRICULA}
          horizontal={!horizontal}
          vertical={horizontal}
        />
        {horizontal ? (
          <>
            <XAxis
              type="number"
              tick={EJE}
              axisLine={false}
              tickLine={false}
              tickFormatter={enHoras}
            />
            <YAxis
              type="category"
              dataKey="nombre"
              tick={{ ...EJE, fill: '#C9E5ED' }}
              axisLine={false}
              tickLine={false}
              width={ANCHO_ETIQUETA}
            />
          </>
        ) : (
          <>
            <XAxis dataKey="nombre" tick={EJE} axisLine={false} tickLine={false} interval={0} />
            <YAxis tick={EJE} axisLine={false} tickLine={false} tickFormatter={enHoras} />
          </>
        )}
        <ReferenceLine
          {...(horizontal ? { x: MINUTOS_LIBRES_ESTADIA } : { y: MINUTOS_LIBRES_ESTADIA })}
          stroke="#EAFF00"
          strokeDasharray="4 4"
          label={{
            value: '45 min libres',
            fill: '#EAFF00',
            fontSize: 10,
            position: 'insideTopRight',
          }}
        />
        {emergente}
        <Bar
          dataKey="m3"
          radius={horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
          barSize={horizontal ? 16 : undefined}
          maxBarSize={54}
        >
          {datos.map((d) => (
            <Cell key={d.nombre} fill={colorTramo(d.m3)} />
          ))}
          <LabelList
            dataKey="m3"
            position={horizontal ? 'right' : 'top'}
            formatter={(v: number) => enHoras(v)}
            style={{ fill: '#8FB3BD', fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/** Tramos de volumen, para poder resumir la nube de puntos en barras. */
function tramosDeVolumen(guias: DocGuia[]): Punto[] {
  const TRAMOS: [number, number, string][] = [
    [0, 2, 'Hasta 2 m³'],
    [2, 4, '2 a 4 m³'],
    [4, 6, '4 a 6 m³'],
    [6, 8, '6 a 8 m³'],
    [8, 12, '8 a 12 m³'],
    [12, 16, '12 a 16 m³'],
    [16, Infinity, 'Más de 16 m³'],
  ];
  return TRAMOS.map(([min, max, nombre]) => {
    const xs = guias.filter(
      (g) => g.minEstadiaObra !== null && g.cantidadM3 > min && g.cantidadM3 <= max,
    );
    // El valor es la estadía promedio en minutos: lo que interesa comparar.
    const prom = xs.length ? xs.reduce((s, g) => s + (g.minEstadiaObra ?? 0), 0) / xs.length : 0;
    return { nombre, m3: Math.round(prom) };
  }).filter((t) => t.m3 > 0);
}

/** Volumen contra estadía en obra. Cada punto es un viaje. */
export function DispersionViajes({ guias }: { guias: DocGuia[] }) {
  const [tipo, setTipo] = useTipoGrafico('dispersion', 'puntos', ['puntos', 'barrasH', 'barras']);
  const porPlanta = new Map<string, { x: number; y: number; g: DocGuia }[]>();
  for (const g of guias) {
    if (g.minEstadiaObra === null || !g.cantidadM3) continue;
    porPlanta.set(g.plantaCodigo, [
      ...(porPlanta.get(g.plantaCodigo) ?? []),
      { x: g.cantidadM3, y: g.minEstadiaObra, g },
    ]);
  }
  const series = [...porPlanta.entries()];
  const total = series.reduce((s, [, v]) => s + v.length, 0);

  return (
    <Tarjeta className="lg:col-span-2">
      <TarjetaTitulo
        accion={
          <div className="flex items-center gap-3">
            {tipo === 'puntos' && (
              <div className="hidden flex-wrap gap-3 text-[11px] text-texto-mute sm:flex">
                {series.map(([planta]) => (
                  <span key={planta} className="flex items-center gap-1.5">
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ background: COLOR_PLANTA[planta] ?? ACENTO }}
                    />
                    {planta}
                  </span>
                ))}
              </div>
            )}
            <SelectorTipo
              tipo={tipo}
              alCambiar={setTipo}
              disponibles={['puntos', 'barrasH', 'barras']}
            />
          </div>
        }
      >
        {tipo === 'puntos'
          ? 'Volumen y estadía en obra por viaje'
          : 'Estadía promedio en obra según el volumen del viaje'}
      </TarjetaTitulo>
      <div className="h-72 p-4">
        {total === 0 ? (
          <Vacio titulo="Sin viajes con horarios completos" />
        ) : tipo !== 'puntos' ? (
          <GraficoTramos datos={tramosDeVolumen(guias)} horizontal={tipo === 'barrasH'} />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 10, right: 16, bottom: 4, left: -12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CUADRICULA} />
              <XAxis
                type="number"
                dataKey="x"
                name="m³"
                unit=" m³"
                tick={EJE}
                axisLine={{ stroke: 'rgba(255,255,255,.1)' }}
                tickLine={false}
              />
              <YAxis
                type="number"
                dataKey="y"
                tick={EJE}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v: number) => `${(v / 60).toFixed(1)} h`}
              />
              <ZAxis range={[50, 50]} />
              <ReferenceLine
                y={MINUTOS_LIBRES_ESTADIA}
                stroke="#EAFF00"
                strokeDasharray="4 4"
                label={{
                  value: '45 min libres',
                  fill: '#EAFF00',
                  fontSize: 10,
                  position: 'insideTopRight',
                }}
              />
              <Tooltip
                {...TOOLTIP}
                cursor={{ strokeDasharray: '3 3', stroke: 'rgba(255,255,255,.2)' }}
                content={({ payload }) => {
                  const p = payload?.[0]?.payload as { g: DocGuia } | undefined;
                  if (!p) return null;
                  const g = p.g;
                  return (
                    <Caja
                      titulo={`#${g.folio} · ${g.plantaCodigo}`}
                      filas={[
                        ['Fecha', ffecha(g.fecha)],
                        ['Cliente', g.clienteNombre ?? '—'],
                        ['Volumen', `${fm3(g.cantidadM3)} m³`],
                        ['En obra', formatearMinutos(g.minEstadiaObra)],
                        ...((g.bloquesCodelco ?? 0) > 0
                          ? ([['Sobrestadía', `${g.bloquesCodelco} bloques`]] as [string, string][])
                          : []),
                      ]}
                    />
                  );
                }}
              />
              {series.map(([planta, datos]) => (
                <Scatter
                  key={planta}
                  name={planta}
                  data={datos}
                  fill={COLOR_PLANTA[planta] ?? ACENTO}
                  fillOpacity={0.7}
                  shape="circle"
                />
              ))}
            </ScatterChart>
          </ResponsiveContainer>
        )}
      </div>
    </Tarjeta>
  );
}
